import React, { useState, useEffect, useRef } from 'react';
import {
  User, Save, Mail, Upload, X, Linkedin, Music, Instagram, MessageCircle, Twitter, Github, Globe,
  Settings, Lock, Shield, Key, AlertTriangle, UserMinus, Smartphone, Eye, EyeOff,
  CheckCircle, XCircle, Clock, Send, History, Camera, Briefcase, Award, Calendar,
  Bell, Palette, Moon, Sun, Monitor, Activity, MapPin, Phone, Building, Heart, Star,
  ImagePlus, Trash2, RefreshCw, Download, FileText, BadgeCheck, Fingerprint, LogOut,
  Languages, Volume2, VolumeX, Laptop, CreditCard, Gift, Sparkles, Zap, Coffee,
  Wifi, WifiOff, CircleDot, Trophy, Target, TrendingUp, Brain, Gamepad2, Headphones,
  Copy, ShieldAlert, AlertCircle, ChevronRight, Crown, Layers, Pencil, Link2, Plus,
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField } from '../../components/ui/jewel-modal';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ══════════════════════════════════════════════════════════════════════
   PRIMITIVES — Jewel & Luxury bento vocabulary
   ══════════════════════════════════════════════════════════════════════ */

const ROLE_META = {
  superadmin: { label: 'Super Admin', hex: '#F59E0B', icon: Crown },
  executive:  { label: 'Executive',   hex: '#8A2BE2', icon: Shield },
  admin:      { label: 'Admin',       hex: '#00E5FF', icon: Shield },
  manager:    { label: 'Manager',     hex: '#10B981', icon: Star },
  staff:      { label: 'Staff',       hex: '#94A3B8', icon: Coffee },
};

const BentoCard = ({ children, className = '', accent = '#00E5FF', tone = 'default', ...props }) => (
  <div
    className={`relative overflow-hidden rounded-3xl border border-white/[0.06] backdrop-blur-md transition-all hover:border-white/[0.10] ${className}`}
    style={{
      background: tone === 'danger'
        ? 'linear-gradient(140deg, rgba(15,10,15,0.7) 0%, rgba(20,8,10,0.85) 100%)'
        : 'linear-gradient(140deg, rgba(10,10,20,0.65) 0%, rgba(8,8,18,0.85) 100%)',
      boxShadow: `0 20px 50px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.03)`,
    }}
    {...props}
  >
    <div className="pointer-events-none absolute -top-24 -right-16 w-56 h-56 rounded-full blur-[100px] opacity-40"
      style={{ background: `radial-gradient(circle, ${accent}30 0%, transparent 70%)` }} />
    <div className="relative">{children}</div>
  </div>
);

const BentoHeader = ({ icon: Icon, title, subtitle, accent = '#00E5FF', right }) => (
  <div className="px-6 pt-5 pb-4 flex items-center gap-3">
    {Icon && (
      <div className="relative w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
        style={{
          background: `linear-gradient(135deg, ${accent}25, rgba(138,43,226,0.15))`,
          boxShadow: `0 0 20px -4px ${accent}55, inset 0 1px 0 rgba(255,255,255,0.06)`,
        }}>
        <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={1.8} />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-[10px] tracking-[0.22em] uppercase text-white/40 font-medium">{subtitle}</p>
      <h3 className="text-white text-[15px] font-light tracking-tight leading-tight">{title}</h3>
    </div>
    {right}
  </div>
);

const Divider = () => (
  <div className="mx-6 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
);

const JewelTextField = ({ label, value, onChange, placeholder, type = 'text', accent = '#00E5FF', icon: Icon, ...rest }) => (
  <div className="space-y-1.5">
    {label && <label className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium">{label}</label>}
    <div className="relative">
      {Icon && <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />}
      <input
        type={type}
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-2.5 bg-black/40 border border-white/[0.07] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-white/20 focus:bg-black/60 transition-all`}
        style={{ '--tw-ring-color': accent }}
        onFocus={(e) => { e.currentTarget.style.borderColor = `${accent}55`; e.currentTarget.style.boxShadow = `0 0 0 3px ${accent}15`; }}
        onBlur={(e) => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.boxShadow = ''; }}
        {...rest}
      />
    </div>
  </div>
);

const JewelTextArea = ({ label, value, onChange, placeholder, rows = 4, maxLength, accent = '#00E5FF', ...rest }) => (
  <div className="space-y-1.5">
    {label && <label className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium">{label}</label>}
    <textarea
      value={value ?? ''}
      onChange={onChange}
      rows={rows}
      maxLength={maxLength}
      placeholder={placeholder}
      className="w-full px-4 py-3 bg-black/40 border border-white/[0.07] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-white/20 focus:bg-black/60 transition-all resize-none"
      onFocus={(e) => { e.currentTarget.style.borderColor = `${accent}55`; e.currentTarget.style.boxShadow = `0 0 0 3px ${accent}15`; }}
      onBlur={(e) => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.boxShadow = ''; }}
      {...rest}
    />
    {maxLength && <p className="text-[10px] text-white/30 text-right">{(value || '').length} / {maxLength}</p>}
  </div>
);

const LuxuryButton = ({ onClick, disabled, loading, icon: Icon, children, tone = 'primary', className = '', accent = '#00E5FF', ...rest }) => {
  const styles = {
    primary: { bg: `linear-gradient(135deg, ${accent}, #8A2BE2)`, color: '#0a0a14', border: 'transparent' },
    ghost:   { bg: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.85)', border: 'rgba(255,255,255,0.08)' },
    danger:  { bg: 'rgba(244,63,94,0.10)', color: '#fca5a5', border: 'rgba(244,63,94,0.30)' },
    outline: { bg: 'transparent', color: accent, border: `${accent}40` },
  }[tone] || {};
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`group relative inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-[11px] font-medium tracking-[0.15em] uppercase overflow-hidden transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5 ${className}`}
      style={{ background: styles.bg, color: styles.color, border: `1px solid ${styles.border}`, boxShadow: tone === 'primary' ? `0 12px 30px -10px ${accent}66` : 'none' }}
      {...rest}
    >
      {tone === 'primary' && (
        <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
      )}
      <span className="relative flex items-center gap-2">
        {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : Icon ? <Icon className="w-3.5 h-3.5" /> : null}
        {children}
      </span>
    </button>
  );
};

const StatChip = ({ icon: Icon, label, value, accent = '#00E5FF' }) => (
  <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3.5">
    <div className="pointer-events-none absolute -top-8 -right-6 w-24 h-24 rounded-full blur-[60px] opacity-40" style={{ background: accent }} />
    <div className="relative flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}18`, border: `1px solid ${accent}30` }}>
        <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={1.8} />
      </div>
      <div className="min-w-0">
        <p className="text-white text-lg font-light leading-none tabular-nums">{value}</p>
        <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 mt-1">{label}</p>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════════════════════ */

const ProfileAndSettingsPage = ({ getAuthHeaders, currentUser, onProfileUpdate, onLogout }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const role = ROLE_META[currentUser?.role] || ROLE_META.staff;

  // ═══ State ═══
  const [profileData, setProfileData] = useState({
    display_name: '', bio: '', contact_email: '', phone: '', location: '', department: '',
    avatar: '', banner_url: '', cover_color: 'cyan', interests: [], skills: [],
    custom_status: '', work_schedule: '',
    socials: { linkedin: '', discord: '', instagram: '', spotify: '', twitter: '', github: '', website: '', roblox: '' }
  });
  const [spotifyStatus, setSpotifyStatus] = useState({ connected: false });
  const [robloxStatus, setRobloxStatus] = useState({ connected: false });
  const [robloxConnectInput, setRobloxConnectInput] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [newSkill, setNewSkill] = useState('');
  const [newInterest, setNewInterest] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [usernameData, setUsernameData] = useState({ new_username: '' });
  const [emailData, setEmailData] = useState({ new_email: '' });
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPassword, setShowPassword] = useState({ current: false, new: false, confirm: false });

  const [preferences, setPreferences] = useState({
    theme: 'dark',
    notifications_email: true,
    notifications_push: true,
    show_online_status: true,
    sound_enabled: true,
    language: 'en',
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    focus_mode: false,
    timezone: (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC') || 'UTC',
  });

  const [loginHistory, setLoginHistory] = useState([]);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [processing2FA, setProcessing2FA] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [totpSecret, setTotpSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');

  const [showResignModal, setShowResignModal] = useState(false);
  const [resignationData, setResignationData] = useState({ reason: '', notice_period: '2_weeks', last_working_day: '', feedback: '' });
  const [myResignation, setMyResignation] = useState(null);

  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('available');

  const [teamPublished, setTeamPublished] = useState(false);
  const [teamPublishLoading, setTeamPublishLoading] = useState(false);

  const STATUS_OPTIONS = [
    { id: 'available', label: 'Available', icon: CircleDot, hex: '#10B981' },
    { id: 'busy',      label: 'Busy',      icon: XCircle,    hex: '#F43F5E' },
    { id: 'meeting',   label: 'Meeting',   icon: Briefcase,  hex: '#F59E0B' },
    { id: 'break',     label: 'On Break',  icon: Coffee,     hex: '#FB923C' },
    { id: 'dnd',       label: 'Do Not Disturb', icon: WifiOff, hex: '#A855F7' },
    { id: 'away',      label: 'Away',      icon: Clock,      hex: '#94A3B8' },
  ];

  // ═══ Load / persist prefs locally ═══
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('user_preferences') || 'null');
      if (cached && typeof cached === 'object') setPreferences(p => ({ ...p, ...cached }));
    } catch {}
  }, []);
  const savePreferences = () => {
    try {
      localStorage.setItem('user_preferences', JSON.stringify(preferences));
      toast.success('Preferences saved');
    } catch { toast.error('Could not save preferences locally'); }
  };

  useEffect(() => {
    initializeProfileData();
    fetchSecurityData();
    fetchMyResignation();
    fetch2FAStatus();
    fetchTeamPublishStatus();
  }, [currentUser]);

  const initializeProfileData = () => {
    const safeSocials = currentUser?.socials || {};
    const socials = {
      linkedin: safeSocials.linkedin || '', discord: safeSocials.discord || '',
      instagram: safeSocials.instagram || '', spotify: safeSocials.spotify || '',
      twitter: safeSocials.twitter || '', github: safeSocials.github || '',
      website: safeSocials.website || '', roblox: safeSocials.roblox || '',
    };
    setProfileData({
      display_name: currentUser?.display_name || '',
      bio: currentUser?.bio || '',
      contact_email: currentUser?.contact_email || currentUser?.email || '',
      phone: currentUser?.phone || '',
      location: currentUser?.location || '',
      department: currentUser?.department || '',
      avatar: currentUser?.avatar || '',
      banner_url: currentUser?.banner_url || '',
      cover_color: currentUser?.cover_color || 'cyan',
      interests: currentUser?.interests || [],
      skills: currentUser?.skills || [],
      custom_status: currentUser?.custom_status || '',
      work_schedule: currentUser?.work_schedule || '',
      socials,
    });
    const h = getAuthHeaders();
    if (h) {
      axios.get(`${BACKEND_URL}/api/spotify/status`, { headers: h }).then(r => setSpotifyStatus(r.data)).catch(() => {});
      axios.get(`${BACKEND_URL}/api/roblox/status`, { headers: h }).then(r => setRobloxStatus(r.data)).catch(() => {});
    }

    if (currentUser?.avatar) {
      const p = currentUser.avatar;
      if (p.startsWith('/api/uploads/')) setAvatarPreview(`${BACKEND_URL}${p}`);
      else if (p.startsWith('/uploads/')) setAvatarPreview(`${BACKEND_URL}/api${p}`);
      else if (p.startsWith('http')) setAvatarPreview(p);
      else setAvatarPreview(`${BACKEND_URL}${p}`);
      setAvatarLoadFailed(false);
    }
    if (currentUser?.banner_url) {
      const bp = currentUser.banner_url;
      if (bp.startsWith('http')) setBannerPreview(bp);
      else if (bp.startsWith('/api/')) setBannerPreview(`${BACKEND_URL}${bp}`);
      else setBannerPreview(`${BACKEND_URL}/api${bp}`);
    }
    setHasUnsavedChanges(false);
  };

  const fetchTeamPublishStatus = async () => {
    try {
      const h = getAuthHeaders(); if (!h) return;
      const res = await axios.get(`${BACKEND_URL}/api/staff-profile-overrides/me`, { headers: h });
      setTeamPublished(!!res.data?.published);
    } catch {}
  };
  const toggleTeamPublish = async (next) => {
    setTeamPublishLoading(true);
    try {
      const h = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/staff-profile-overrides/me`, { publish: next, department: profileData.department || 'other' }, { headers: h });
      setTeamPublished(next);
      toast.success(next ? "You're on the public Team page" : 'Removed from the Team page');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setTeamPublishLoading(false); }
  };

  const fetch2FAStatus = async () => {
    try {
      const h = getAuthHeaders(); if (!h) return;
      const r = await axios.get(`${BACKEND_URL}/api/2fa/status`, { headers: h });
      setTwoFactorEnabled(r.data.enabled);
    } catch {}
  };
  const fetchSecurityData = async () => {
    try {
      const h = getAuthHeaders(); if (!h) return;
      const [hi, se] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/account/login-history`, { headers: h }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/account/sessions`, { headers: h }).catch(() => ({ data: [] })),
      ]);
      setLoginHistory(hi.data || []);
      setSessions(se.data || []);
    } catch {}
  };
  const fetchMyResignation = async () => {
    try {
      const h = getAuthHeaders(); if (!h) return;
      const r = await axios.get(`${BACKEND_URL}/api/admin/account/my-resignation`, { headers: h });
      setMyResignation(r.data);
    } catch {}
  };

  // ═══ File / field handlers ═══
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Image must be under 5MB');
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => { setAvatarPreview(reader.result); setAvatarLoadFailed(false); setHasUnsavedChanges(true); };
    reader.readAsDataURL(file);
    toast.success('Photo selected — click Save to upload');
  };
  const removeAvatar = () => {
    setAvatarFile(null); setAvatarPreview(null);
    setProfileData({ ...profileData, avatar: '' }); setHasUnsavedChanges(true);
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };
  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Banner must be under 5MB');
    setBannerFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => { setBannerPreview(ev.target.result); setHasUnsavedChanges(true); };
    reader.readAsDataURL(file);
  };
  const removeBanner = () => { setBannerPreview(null); setBannerFile(null); setProfileData({ ...profileData, banner_url: '' }); setHasUnsavedChanges(true); };
  const handleProfileChange = (field, v) => { setProfileData({ ...profileData, [field]: v }); setHasUnsavedChanges(true); };
  const handleSocialChange = (k, v) => { setProfileData({ ...profileData, socials: { ...profileData.socials, [k]: v } }); setHasUnsavedChanges(true); };

  const addSkill = () => { if (newSkill.trim() && !profileData.skills?.includes(newSkill.trim())) { setProfileData({ ...profileData, skills: [...(profileData.skills || []), newSkill.trim()] }); setNewSkill(''); setHasUnsavedChanges(true); } };
  const removeSkill = (s) => { setProfileData({ ...profileData, skills: profileData.skills.filter(x => x !== s) }); setHasUnsavedChanges(true); };
  const addInterest = () => { if (newInterest.trim() && !profileData.interests?.includes(newInterest.trim())) { setProfileData({ ...profileData, interests: [...(profileData.interests || []), newInterest.trim()] }); setNewInterest(''); setHasUnsavedChanges(true); } };
  const removeInterest = (i) => { setProfileData({ ...profileData, interests: profileData.interests.filter(x => x !== i) }); setHasUnsavedChanges(true); };

  // ═══ API actions ═══
  const handleUpdateProfile = async () => {
    setIsSaving(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return toast.error('Session expired');

      let avatarUrl = profileData.avatar;
      let bannerUrl = profileData.banner_url;

      if (avatarFile) {
        const fd = new FormData(); fd.append('avatar', avatarFile);
        try {
          const r = await axios.post(`${BACKEND_URL}/api/admin/upload-avatar`, fd, { headers: { Authorization: headers.Authorization } });
          avatarUrl = r.data.avatar_url;
        } catch { toast.error('Photo upload failed'); }
      }
      if (bannerFile) {
        const fd = new FormData(); fd.append('avatar', bannerFile);
        try {
          const r = await axios.post(`${BACKEND_URL}/api/admin/upload-avatar`, fd, { headers: { Authorization: headers.Authorization } });
          bannerUrl = r.data.avatar_url;
        } catch { toast.error('Banner upload failed'); }
      }

      await axios.put(`${BACKEND_URL}/api/admin/profile`, {
        display_name: profileData.display_name, bio: profileData.bio, contact_email: profileData.contact_email,
        phone: profileData.phone, location: profileData.location, department: profileData.department,
        avatar: avatarUrl, banner_url: bannerUrl, cover_color: profileData.cover_color,
        skills: profileData.skills, interests: profileData.interests, socials: profileData.socials,
        custom_status: profileData.custom_status, work_schedule: profileData.work_schedule,
      }, { headers });

      toast.success('Profile saved');
      setHasUnsavedChanges(false); setAvatarFile(null); setBannerFile(null);
      onProfileUpdate && onProfileUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setIsSaving(false); }
  };

  const handleUpdateUsername = async () => {
    if (!usernameData.new_username) return toast.error('Please enter a new username');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/account/username`, usernameData, { headers: getAuthHeaders() });
      toast.success('Username updated — logging out…');
      setTimeout(() => { localStorage.removeItem('staff_token'); window.location.href = '/staff/login'; }, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleUpdateEmail = async () => {
    if (!emailData.new_email) return toast.error('Please enter a new email');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/account/email`, emailData, { headers: getAuthHeaders() });
      toast.success('Email updated');
      setEmailData({ new_email: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleUpdatePassword = async () => {
    if (!passwordData.current_password || !passwordData.new_password) return toast.error('Please fill all fields');
    if (passwordData.new_password !== passwordData.confirm_password) return toast.error('Passwords do not match');
    if (passwordData.new_password.length < 8) return toast.error('Password must be 8+ characters');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/account/password`, {
        current_password: passwordData.current_password, new_password: passwordData.new_password,
      }, { headers: getAuthHeaders() });
      toast.success('Password updated');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const initiate2FASetup = async () => {
    try {
      const r = await axios.post(`${BACKEND_URL}/api/2fa/enable`, {}, { headers: getAuthHeaders() });
      setQrCodeData(r.data.qr_code); setTotpSecret(r.data.secret); setShow2FASetup(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const verify2FASetup = async () => {
    if (verifyCode.length !== 6) return toast.error('Enter a 6-digit code');
    setProcessing2FA(true);
    try {
      await axios.post(`${BACKEND_URL}/api/2fa/verify-setup`, { code: verifyCode }, { headers: getAuthHeaders() });
      toast.success('Two-Factor Authentication enabled');
      setTwoFactorEnabled(true); setShow2FASetup(false);
      setQrCodeData(null); setTotpSecret(''); setVerifyCode('');
      fetch2FAStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setProcessing2FA(false); }
  };
  const handle2FADisable = async () => {
    if (!window.confirm('Disable Two-Factor Authentication?')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/2fa/disable`, {}, { headers: getAuthHeaders() });
      toast.success('2FA disabled'); setTwoFactorEnabled(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSubmitResignation = async () => {
    if (!resignationData.reason || !resignationData.last_working_day) return toast.error('Please fill required fields');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/account/resignation`, resignationData, { headers: getAuthHeaders() });
      toast.success('Resignation submitted');
      setShowResignModal(false); fetchMyResignation();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleWithdrawResignation = async () => {
    if (!window.confirm('Withdraw your resignation?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/account/resignation`, { headers: getAuthHeaders() });
      toast.success('Resignation withdrawn'); setMyResignation(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ═══ Helpers ═══
  const getPasswordStrength = (p) => {
    let s = 0;
    if (p.length >= 8) s++; if (p.length >= 12) s++;
    if (/[A-Z]/.test(p)) s++; if (/[a-z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++; if (/[^A-Za-z0-9]/.test(p)) s++;
    if (s < 3) return { label: 'Weak', hex: '#F43F5E', percent: 33 };
    if (s < 5) return { label: 'Medium', hex: '#F59E0B', percent: 66 };
    return { label: 'Strong', hex: '#10B981', percent: 100 };
  };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const calculateCompleteness = () => {
    let c = 0; const total = 10;
    if (profileData.display_name) c++;
    if (profileData.bio && profileData.bio.length > 10) c++;
    if (profileData.contact_email) c++;
    if (profileData.avatar || avatarPreview) c++;
    if (profileData.phone) c++;
    if (profileData.location) c++;
    if (profileData.department) c++;
    if (profileData.socials?.linkedin) c++;
    if (profileData.skills?.length > 0) c++;
    if (Object.values(profileData.socials || {}).filter(Boolean).length >= 3) c++;
    return Math.round((c / total) * 100);
  };

  const passwordStrength = getPasswordStrength(passwordData.new_password);
  const profileCompleteness = calculateCompleteness();
  const connectedCount = Object.values(profileData.socials || {}).filter(Boolean).length + (spotifyStatus.connected ? 1 : 0) + (robloxStatus.connected ? 1 : 0);

  const TABS = [
    { id: 'profile',     label: 'Identity',   icon: User,       hex: '#00E5FF' },
    { id: 'connections', label: 'Connections', icon: Link2,     hex: '#F472B6' },
    { id: 'account',     label: 'Account',    icon: Settings,   hex: '#8A2BE2' },
    { id: 'security',    label: 'Security',   icon: Shield,     hex: '#10B981' },
    { id: 'preferences', label: 'Preferences', icon: Palette,   hex: '#A855F7' },
    { id: 'activity',    label: 'Activity',   icon: Activity,   hex: '#F59E0B' },
    { id: 'employment',  label: 'Employment', icon: Briefcase,  hex: '#38BDF8' },
    { id: 'warnings',    label: 'HR Record',  icon: ShieldAlert, hex: '#F43F5E' },
  ];

  /* ══════════════════════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen pb-12 relative" data-testid="profile-settings-page">
      {/* Ambient background */}
      <div className="fixed top-0 left-1/4 w-[700px] h-[500px] bg-[#00E5FF]/[0.03] rounded-full blur-[180px] pointer-events-none -z-10" />
      <div className="fixed bottom-0 right-1/4 w-[600px] h-[400px] bg-[#8A2BE2]/[0.04] rounded-full blur-[160px] pointer-events-none -z-10" />
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        .anim-fadeInUp { animation: fadeInUp 0.4s ease-out; }
      `}</style>

      {/* ═══ FLOATING SAVE ═══ */}
      {hasUnsavedChanges && (
        <div className="fixed bottom-6 right-6 z-50 anim-fadeInUp">
          <LuxuryButton onClick={handleUpdateProfile} loading={isSaving} icon={Save} tone="primary" accent="#00E5FF">
            {isSaving ? 'Saving…' : 'Save Changes'}
          </LuxuryButton>
        </div>
      )}

      {/* ═══════════════════════════════════════════
          HERO — Bento glass with role-tinted glow
          ═══════════════════════════════════════════ */}
      <div className="relative overflow-hidden rounded-[32px] border border-white/[0.06] mb-6"
        style={{
          background: 'linear-gradient(140deg, rgba(10,10,20,0.75) 0%, rgba(8,8,18,0.9) 100%)',
          boxShadow: `0 30px 80px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)`,
        }} data-testid="profile-hero">

        {/* Banner layer */}
        <div className="relative h-52 overflow-hidden">
          {(bannerPreview || profileData.banner_url) ? (
            <>
              <img src={bannerPreview || (profileData.banner_url?.startsWith('http') ? profileData.banner_url : `${BACKEND_URL}${profileData.banner_url}`)}
                alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(5,5,10,0.30) 0%, rgba(5,5,10,0.85) 100%)' }} />
            </>
          ) : (
            <>
              <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${role.hex}30 0%, rgba(138,43,226,0.30) 100%)` }} />
              <div className="pointer-events-none absolute -top-32 -left-24 w-96 h-96 rounded-full blur-[130px]" style={{ background: `${role.hex}55` }} />
              <div className="pointer-events-none absolute -bottom-32 -right-24 w-96 h-96 rounded-full blur-[130px]" style={{ background: 'rgba(0,229,255,0.35)' }} />
            </>
          )}
          <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${role.hex}88, transparent)` }} />

          {/* Banner controls */}
          <div className="absolute top-4 right-4 flex gap-2 z-10">
            <input ref={bannerInputRef} type="file" accept="image/*" className="hidden" onChange={handleBannerChange} />
            <button onClick={() => bannerInputRef.current?.click()} data-testid="upload-banner-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md text-white/80 hover:text-white text-[10px] font-medium tracking-[0.15em] uppercase border border-white/[0.10] hover:border-white/25 transition-all">
              <ImagePlus className="w-3 h-3" /> Banner
            </button>
            {(bannerPreview || profileData.banner_url) && (
              <button onClick={removeBanner} data-testid="remove-banner-btn"
                className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-rose-300 border border-white/[0.10] hover:border-rose-400/40 transition-all">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Identity strip */}
        <div className="relative px-8 pb-6 -mt-20">
          <div className="flex items-end gap-6 flex-wrap md:flex-nowrap">

            {/* Avatar */}
            <div className="relative group flex-shrink-0">
              <div className="relative w-32 h-32 md:w-36 md:h-36 rounded-[28px] overflow-hidden border-[4px] border-[#0a0a14] bg-slate-900"
                style={{ boxShadow: `0 20px 40px -12px rgba(0,0,0,0.7), 0 0 32px -6px ${role.hex}` }}>
                {avatarPreview && !avatarLoadFailed ? (
                  <img src={avatarPreview} alt="" className="w-full h-full object-cover" onError={() => setAvatarLoadFailed(true)} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${role.hex}44, rgba(138,43,226,0.4))` }}>
                    <User className="w-14 h-14 text-white/25" strokeWidth={1.2} />
                  </div>
                )}
              </div>
              <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
              <button onClick={() => avatarInputRef.current?.click()}
                className="absolute inset-0 rounded-[28px] flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all cursor-pointer">
                <ImagePlus className="w-7 h-7 text-white mb-1.5" strokeWidth={1.4} />
                <span className="text-white text-[10px] tracking-[0.15em] uppercase font-medium">Upload</span>
              </button>
              {avatarPreview && (
                <button onClick={(e) => { e.stopPropagation(); removeAvatar(); }}
                  className="absolute -top-2 -right-2 w-7 h-7 bg-rose-500/90 hover:bg-rose-500 rounded-full flex items-center justify-center text-white shadow-lg ring-2 ring-[#0a0a14]">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              {/* Online dot */}
              <div className="absolute bottom-1 right-1 w-5 h-5 rounded-full border-[3px] border-[#0a0a14]"
                style={{ background: STATUS_OPTIONS.find(s => s.id === statusType)?.hex, boxShadow: `0 0 10px ${STATUS_OPTIONS.find(s => s.id === statusType)?.hex}` }} />
            </div>

            {/* Name + role + meta */}
            <div className="flex-1 min-w-0 pb-2">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-semibold tracking-[0.18em] uppercase border"
                  style={{ color: role.hex, background: `${role.hex}12`, borderColor: `${role.hex}35` }}>
                  <role.icon className="w-2.5 h-2.5" /> {role.label}
                </span>
                {profileData.department && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-medium tracking-[0.15em] uppercase text-white/60 bg-white/[0.04] border border-white/[0.08]">
                    <Building className="w-2.5 h-2.5" /> {profileData.department}
                  </span>
                )}
              </div>
              <h1 className="text-white text-3xl md:text-4xl font-light tracking-tight leading-tight">
                {profileData.display_name || currentUser?.username}
              </h1>
              <p className="text-white/40 text-sm mt-0.5">@{currentUser?.username}</p>
              {profileData.custom_status && (
                <p className="text-white/75 text-sm italic mt-2">"{profileData.custom_status}"</p>
              )}

              <div className="flex items-center gap-2 flex-wrap mt-3">
                {profileData.location && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-white/60 px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/[0.06]">
                    <MapPin className="w-3 h-3" /> {profileData.location}
                  </span>
                )}
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/staff/team/${currentUser?.username || ''}`;
                    navigator.clipboard.writeText(url); toast.success('Profile link copied');
                  }}
                  data-testid="share-profile-btn"
                  className="inline-flex items-center gap-1.5 text-[11px] text-[#00E5FF] px-2.5 py-1 rounded-full bg-[#00E5FF]/[0.08] border border-[#00E5FF]/25 hover:bg-[#00E5FF]/[0.15] transition-all">
                  <Copy className="w-3 h-3" /> Share
                </button>
              </div>
            </div>

            {/* Completeness ring + quick stats */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="relative w-20 h-20">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
                  <circle cx="40" cy="40" r="34" fill="none"
                    stroke={profileCompleteness >= 80 ? '#10b981' : profileCompleteness >= 50 ? '#F59E0B' : '#F43F5E'}
                    strokeWidth="4" strokeDasharray={`${profileCompleteness * 2.13} 213`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-white text-lg font-light tabular-nums leading-none">{profileCompleteness}%</span>
                  <span className="text-[8px] tracking-[0.2em] uppercase text-white/40 mt-0.5">Profile</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════
          TABS
          ═══════════════════════════════════════════ */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0A0A12]/60 backdrop-blur-md p-1 mb-6">
        <div className="flex gap-1 overflow-x-auto jewel-scroll">
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`profile-tab-${tab.id}`}
                className="relative group flex items-center gap-2 px-4 py-2.5 rounded-xl text-[11px] font-medium tracking-[0.12em] uppercase whitespace-nowrap transition-all"
                style={{
                  background: active ? `linear-gradient(135deg, ${tab.hex}18, rgba(138,43,226,0.08))` : 'transparent',
                  color: active ? '#fff' : 'rgba(255,255,255,0.45)',
                  border: active ? `1px solid ${tab.hex}35` : '1px solid transparent',
                  boxShadow: active ? `0 4px 20px -6px ${tab.hex}55` : 'none',
                }}
              >
                <tab.icon className="w-3.5 h-3.5" strokeWidth={active ? 2 : 1.6} style={{ color: active ? tab.hex : 'currentColor' }} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══════════════════════════════════════════
          TAB CONTENT
          ═══════════════════════════════════════════ */}

      {/* ─── IDENTITY ─── */}
      {activeTab === 'profile' && (
        <div className="grid lg:grid-cols-3 gap-4" data-testid="tab-profile">
          {/* LEFT column — Basics + status + skills */}
          <div className="lg:col-span-2 space-y-4">
            <BentoCard accent="#00E5FF">
              <BentoHeader icon={User} title="Basic Information" subtitle="Public identity" accent="#00E5FF" />
              <Divider />
              <div className="px-6 py-5 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <JewelTextField label="Display Name" value={profileData.display_name} onChange={(e) => handleProfileChange('display_name', e.target.value)} placeholder="Your full name" data-testid="input-display-name" />
                  <JewelTextField label="Contact Email" type="email" icon={Mail} value={profileData.contact_email} onChange={(e) => handleProfileChange('contact_email', e.target.value)} placeholder="you@company.com" />
                  <JewelTextField label="Phone" type="tel" icon={Phone} value={profileData.phone} onChange={(e) => handleProfileChange('phone', e.target.value)} placeholder="+44 7xxx xxx xxx" />
                  <JewelTextField label="Location" icon={MapPin} value={profileData.location} onChange={(e) => handleProfileChange('location', e.target.value)} placeholder="London, UK" />
                  <div className="md:col-span-2">
                    <JewelTextField label="Department" icon={Building} value={profileData.department} onChange={(e) => handleProfileChange('department', e.target.value)} placeholder="Engineering, Marketing…" />
                  </div>
                </div>
                <JewelTextArea label="Bio" value={profileData.bio} onChange={(e) => handleProfileChange('bio', e.target.value)} rows={4} maxLength={500} placeholder="Tell your colleagues about yourself…" />
              </div>
            </BentoCard>

            {/* Status */}
            <BentoCard accent="#F59E0B">
              <BentoHeader icon={Zap} title="Status & Vibe" subtitle="Right now" accent="#F59E0B" />
              <Divider />
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-2 block">Presence</label>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5">
                    {STATUS_OPTIONS.map(s => {
                      const active = statusType === s.id;
                      const SI = s.icon;
                      return (
                        <button key={s.id} onClick={() => setStatusType(s.id)}
                          className="group flex flex-col items-center gap-1.5 py-2.5 rounded-xl border transition-all"
                          style={{
                            background: active ? `${s.hex}12` : 'rgba(255,255,255,0.02)',
                            borderColor: active ? `${s.hex}40` : 'rgba(255,255,255,0.06)',
                            color: active ? s.hex : 'rgba(255,255,255,0.5)',
                          }}>
                          <SI className="w-3.5 h-3.5" />
                          <span className="text-[9px] tracking-wider uppercase font-medium">{s.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <JewelTextField label="Custom Status" accent="#F59E0B" value={profileData.custom_status} onChange={(e) => handleProfileChange('custom_status', e.target.value)} placeholder="What are you up to?" maxLength={100} data-testid="custom-status-input" />
                <JewelTextField label="Work Schedule" accent="#F59E0B" icon={Clock} value={profileData.work_schedule} onChange={(e) => handleProfileChange('work_schedule', e.target.value)} placeholder="Mon–Fri · 9am–5pm" data-testid="work-schedule-input" />
                <JewelTextField label="Status Message (temporary)" accent="#F59E0B" value={statusMessage} onChange={(e) => setStatusMessage(e.target.value)} placeholder="A quick note for teammates…" maxLength={50} />
              </div>
            </BentoCard>

            {/* Skills + Interests */}
            <div className="grid md:grid-cols-2 gap-4">
              <BentoCard accent="#EAB308">
                <BentoHeader icon={Zap} title="Skills" subtitle="Your expertise" accent="#EAB308" />
                <Divider />
                <div className="px-6 py-5 space-y-3">
                  <div className="flex gap-2">
                    <input type="text" value={newSkill} onChange={(e) => setNewSkill(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                      className="flex-1 px-3 py-2 bg-black/40 border border-white/[0.07] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-amber-400/40"
                      placeholder="Add a skill…" />
                    <button onClick={addSkill} className="px-3 py-2 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 transition-all">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 min-h-[40px]">
                    {profileData.skills?.length === 0 ? (
                      <p className="text-white/30 text-xs">No skills yet</p>
                    ) : profileData.skills?.map(s => (
                      <span key={s} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-[11px] font-medium border border-amber-500/25 text-amber-300 bg-amber-500/10">
                        {s}
                        <button onClick={() => removeSkill(s)} className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-amber-500/20"><X className="w-2.5 h-2.5" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              </BentoCard>

              <BentoCard accent="#F43F5E">
                <BentoHeader icon={Heart} title="Interests" subtitle="Off-work passions" accent="#F43F5E" />
                <Divider />
                <div className="px-6 py-5 space-y-3">
                  <div className="flex gap-2">
                    <input type="text" value={newInterest} onChange={(e) => setNewInterest(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addInterest()}
                      className="flex-1 px-3 py-2 bg-black/40 border border-white/[0.07] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-rose-400/40"
                      placeholder="Add an interest…" />
                    <button onClick={addInterest} className="px-3 py-2 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 hover:bg-rose-500/25 transition-all">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 min-h-[40px]">
                    {profileData.interests?.length === 0 ? (
                      <p className="text-white/30 text-xs">No interests yet</p>
                    ) : profileData.interests?.map(i => (
                      <span key={i} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-[11px] font-medium border border-rose-500/25 text-rose-300 bg-rose-500/10">
                        {i}
                        <button onClick={() => removeInterest(i)} className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-rose-500/20"><X className="w-2.5 h-2.5" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              </BentoCard>
            </div>
          </div>

          {/* RIGHT column — Public team + quick stats */}
          <div className="space-y-4">
            <BentoCard accent="#00E5FF">
              <BentoHeader icon={Globe} title="Public Team Page" subtitle="Visibility" accent="#00E5FF"
                right={<Switch checked={teamPublished} onCheckedChange={toggleTeamPublish} disabled={teamPublishLoading} data-testid="team-publish-toggle" className="data-[state=checked]:bg-cyan-500" />} />
              <Divider />
              <div className="px-6 py-5">
                <p className="text-white/70 text-xs leading-relaxed mb-3">
                  {teamPublished
                    ? "You're live on the public /team page. Every update to your photo, bio and socials appears there straight away."
                    : "Publish yourself on the Team page to be discoverable by visitors. Toggle off any time."}
                </p>
                {teamPublished && (
                  <a href="/team" target="_blank" rel="noopener noreferrer" data-testid="view-my-team-card"
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-cyan-300 hover:text-cyan-200 transition-colors">
                    View my card on /team <ChevronRight className="w-3 h-3" />
                  </a>
                )}
              </div>
            </BentoCard>

            <BentoCard accent="#8A2BE2">
              <BentoHeader icon={Activity} title="Snapshot" subtitle="At a glance" accent="#8A2BE2" />
              <Divider />
              <div className="px-6 py-5 grid grid-cols-2 gap-2.5">
                <StatChip icon={Link2} label="Connected" value={connectedCount} accent="#00E5FF" />
                <StatChip icon={Zap} label="Skills" value={profileData.skills?.length || 0} accent="#EAB308" />
                <StatChip icon={Heart} label="Interests" value={profileData.interests?.length || 0} accent="#F43F5E" />
                <StatChip icon={Calendar} label="Since" value={currentUser?.created_at ? new Date(currentUser.created_at).getFullYear() : '—'} accent="#10B981" />
              </div>
            </BentoCard>

            <BentoCard accent="#10B981">
              <BentoHeader icon={CheckCircle} title="Completeness" subtitle="What's missing" accent="#10B981" />
              <Divider />
              <div className="px-6 py-5 space-y-2">
                {[
                  ['Display name', !!profileData.display_name],
                  ['Bio (10+ chars)', !!(profileData.bio && profileData.bio.length > 10)],
                  ['Contact email', !!profileData.contact_email],
                  ['Avatar photo', !!(profileData.avatar || avatarPreview)],
                  ['Location', !!profileData.location],
                  ['Department', !!profileData.department],
                  ['LinkedIn linked', !!profileData.socials?.linkedin],
                  ['Skills listed', profileData.skills?.length > 0],
                ].map(([label, done]) => (
                  <div key={label} className="flex items-center gap-2.5 py-1">
                    {done
                      ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                      : <CircleDot className="w-3.5 h-3.5 text-white/25 flex-shrink-0" />}
                    <span className={`text-xs ${done ? 'text-white/80' : 'text-white/40'}`}>{label}</span>
                  </div>
                ))}
              </div>
            </BentoCard>
          </div>
        </div>
      )}

      {/* ─── CONNECTIONS ─── */}
      {activeTab === 'connections' && (
        <div className="space-y-4" data-testid="tab-connections">
          {/* Featured connectors */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Spotify */}
            <BentoCard accent="#1DB954">
              <BentoHeader icon={Music} title="Spotify" subtitle="Live listening status" accent="#1DB954" />
              <Divider />
              <div className="px-6 py-5">
                {spotifyStatus.connected ? (
                  <>
                    <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#1DB954]/10 border border-[#1DB954]/25 mb-3">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#1DB954]/25">
                        <Music className="w-5 h-5 text-[#1DB954]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{spotifyStatus.spotify_display_name || spotifyStatus.spotify_id}</p>
                        <p className="text-[#1DB954] text-xs">Connected</p>
                      </div>
                    </div>
                    <LuxuryButton tone="danger" icon={X} className="w-full" data-testid="disconnect-spotify-btn"
                      onClick={async () => {
                        try { await axios.delete(`${BACKEND_URL}/api/spotify/disconnect`, { headers: getAuthHeaders() });
                          setSpotifyStatus({ connected: false }); toast.success('Disconnected'); }
                        catch { toast.error('Failed'); }
                      }}>Disconnect</LuxuryButton>
                  </>
                ) : (
                  <>
                    <p className="text-white/60 text-xs leading-relaxed mb-3">Show what you're listening to right now on your profile card. Updates in real time.</p>
                    <LuxuryButton tone="primary" accent="#1DB954" icon={Music} className="w-full" data-testid="connect-spotify-btn"
                      onClick={async () => {
                        try { const r = await axios.get(`${BACKEND_URL}/api/spotify/connect?origin=${encodeURIComponent(window.location.origin)}`, { headers: getAuthHeaders() });
                          window.location.href = r.data.auth_url; }
                        catch (e) { toast.error(e.response?.data?.detail || 'Not configured yet'); }
                      }}>Connect Spotify</LuxuryButton>
                  </>
                )}
              </div>
            </BentoCard>

            {/* Roblox */}
            <BentoCard accent="#F43F5E">
              <BentoHeader icon={Gamepad2} title="Roblox" subtitle="Show in-game presence" accent="#F43F5E" />
              <Divider />
              <div className="px-6 py-5">
                {robloxStatus.connected ? (
                  <>
                    <div className="flex items-center gap-3 p-3 rounded-2xl bg-rose-500/10 border border-rose-500/25 mb-3">
                      {robloxStatus.roblox_avatar_url
                        ? <img src={robloxStatus.roblox_avatar_url} alt="" className="w-11 h-11 rounded-xl ring-1 ring-white/10" />
                        : <div className="w-11 h-11 rounded-xl bg-rose-500/25 flex items-center justify-center"><Gamepad2 className="w-5 h-5 text-rose-300" /></div>}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{robloxStatus.roblox_display_name || robloxStatus.roblox_username}</p>
                        <p className="text-rose-300 text-xs">Connected</p>
                      </div>
                    </div>
                    <LuxuryButton tone="danger" icon={X} className="w-full" data-testid="disconnect-roblox-btn"
                      onClick={async () => {
                        try { await axios.delete(`${BACKEND_URL}/api/roblox/disconnect`, { headers: getAuthHeaders() });
                          setRobloxStatus({ connected: false }); toast.success('Disconnected'); }
                        catch { toast.error('Failed'); }
                      }}>Disconnect</LuxuryButton>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <input type="text" value={robloxConnectInput} onChange={(e) => setRobloxConnectInput(e.target.value)}
                      placeholder="Roblox username" data-testid="roblox-username-input"
                      className="flex-1 px-3 py-2.5 bg-black/40 border border-white/[0.07] rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-rose-400/40" />
                    <LuxuryButton tone="primary" accent="#F43F5E" icon={Gamepad2} data-testid="connect-roblox-btn"
                      onClick={async () => {
                        if (!robloxConnectInput.trim()) return toast.error('Enter a username');
                        try { const r = await axios.post(`${BACKEND_URL}/api/roblox/connect`, { roblox_username: robloxConnectInput.trim() }, { headers: getAuthHeaders() });
                          setRobloxStatus({ connected: true, ...r.data }); setRobloxConnectInput('');
                          toast.success(`Connected as ${r.data.roblox_display_name}`); }
                        catch (e) { toast.error(e.response?.data?.detail || 'Not found'); }
                      }}>Connect</LuxuryButton>
                  </div>
                )}
              </div>
            </BentoCard>
          </div>

          {/* Social links grid */}
          <BentoCard accent="#F472B6">
            <BentoHeader icon={Globe} title="Social Links" subtitle="Where to find you online" accent="#F472B6" />
            <Divider />
            <div className="px-6 py-5 grid md:grid-cols-2 gap-3">
              {[
                { key: 'linkedin',  icon: Linkedin,       hex: '#0A66C2', label: 'LinkedIn',   placeholder: 'linkedin.com/in/yourname' },
                { key: 'twitter',   icon: Twitter,        hex: '#38BDF8', label: 'Twitter / X', placeholder: '@username' },
                { key: 'github',    icon: Github,         hex: '#94A3B8', label: 'GitHub',     placeholder: 'github.com/username' },
                { key: 'instagram', icon: Instagram,      hex: '#EC4899', label: 'Instagram',  placeholder: '@username' },
                { key: 'discord',   icon: MessageCircle,  hex: '#5865F2', label: 'Discord',    placeholder: 'username' },
                { key: 'website',   icon: Globe,          hex: '#00E5FF', label: 'Website',    placeholder: 'https://…' },
              ].map(s => (
                <div key={s.key} className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3.5 transition-all hover:border-white/[0.12]">
                  <span className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl" style={{ background: s.hex }} />
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${s.hex}18`, border: `1px solid ${s.hex}30` }}>
                      <s.icon className="w-3.5 h-3.5" style={{ color: s.hex }} />
                    </div>
                    <span className="text-white text-xs font-medium tracking-wide">{s.label}</span>
                  </div>
                  <input
                    type="text"
                    value={profileData.socials?.[s.key] || ''}
                    onChange={(e) => handleSocialChange(s.key, e.target.value)}
                    placeholder={s.placeholder}
                    className="w-full px-3 py-2 bg-black/40 border border-white/[0.06] rounded-lg text-white text-xs placeholder-white/25 focus:outline-none focus:border-white/20 transition-all"
                  />
                </div>
              ))}
            </div>
          </BentoCard>
        </div>
      )}

      {/* ─── ACCOUNT ─── */}
      {activeTab === 'account' && (
        <div className="space-y-4 max-w-3xl" data-testid="tab-account">
          {/* Info strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Username', value: currentUser?.username, hex: '#00E5FF', icon: User },
              { label: 'Email', value: currentUser?.email || '—', hex: '#F472B6', icon: Mail },
              { label: 'Role', value: role.label, hex: role.hex, icon: role.icon },
              { label: 'Status', value: 'Active', hex: '#10B981', icon: CheckCircle },
            ].map(s => (
              <BentoCard key={s.label} accent={s.hex} className="!rounded-2xl">
                <div className="px-4 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <s.icon className="w-3.5 h-3.5" style={{ color: s.hex }} />
                    <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 font-medium">{s.label}</p>
                  </div>
                  <p className="text-white text-sm font-medium truncate">{s.value}</p>
                </div>
              </BentoCard>
            ))}
          </div>

          {/* Username */}
          <BentoCard accent="#00E5FF">
            <BentoHeader icon={User} title="Change Username" subtitle="Your unique handle" accent="#00E5FF" />
            <Divider />
            <div className="px-6 py-5 space-y-3">
              <JewelTextField value={usernameData.new_username} onChange={(e) => setUsernameData({ new_username: e.target.value })} placeholder="Enter new username" />
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-300 text-xs">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>You'll be logged out and need to sign in with your new username.</span>
              </div>
              <LuxuryButton onClick={handleUpdateUsername} icon={Save} tone="primary" accent="#00E5FF">Update Username</LuxuryButton>
            </div>
          </BentoCard>

          {/* Email */}
          <BentoCard accent="#F472B6">
            <BentoHeader icon={Mail} title="Change Email" subtitle="Where we reach you" accent="#F472B6" />
            <Divider />
            <div className="px-6 py-5 space-y-3">
              <JewelTextField type="email" accent="#F472B6" icon={Mail} value={emailData.new_email} onChange={(e) => setEmailData({ new_email: e.target.value })} placeholder="new.email@company.com" />
              <LuxuryButton onClick={handleUpdateEmail} icon={Save} tone="primary" accent="#F472B6">Update Email</LuxuryButton>
            </div>
          </BentoCard>

          {/* Password */}
          <BentoCard accent="#8A2BE2">
            <BentoHeader icon={Key} title="Change Password" subtitle="Rotate your credentials" accent="#8A2BE2" />
            <Divider />
            <div className="px-6 py-5 space-y-3">
              {[
                { field: 'current_password', label: 'Current password', show: 'current' },
                { field: 'new_password',     label: 'New password',     show: 'new' },
                { field: 'confirm_password', label: 'Confirm new',      show: 'confirm' },
              ].map(p => (
                <div key={p.field} className="relative">
                  <label className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium block mb-1.5">{p.label}</label>
                  <input
                    type={showPassword[p.show] ? 'text' : 'password'}
                    value={passwordData[p.field]}
                    onChange={(e) => setPasswordData({ ...passwordData, [p.field]: e.target.value })}
                    placeholder={p.label}
                    className="w-full px-4 py-2.5 bg-black/40 border border-white/[0.07] rounded-xl text-white text-sm placeholder-white/25 pr-11 focus:outline-none focus:border-violet-400/40 transition-all"
                  />
                  <button type="button" onClick={() => setShowPassword({ ...showPassword, [p.show]: !showPassword[p.show] })}
                    className="absolute right-3 top-9 text-white/40 hover:text-white/80 transition-colors">
                    {showPassword[p.show] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              ))}
              {passwordData.new_password && (
                <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="flex justify-between text-[10px] tracking-[0.18em] uppercase text-white/40 mb-2">
                    <span>Strength</span>
                    <span style={{ color: passwordStrength.hex }} className="font-medium">{passwordStrength.label}</span>
                  </div>
                  <div className="h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${passwordStrength.percent}%`, background: `linear-gradient(90deg, ${passwordStrength.hex}, ${passwordStrength.hex}cc)` }} />
                  </div>
                  <ul className="text-[10px] text-white/40 space-y-1 mt-2.5">
                    {[
                      [passwordData.new_password.length >= 8, '8+ characters'],
                      [/[A-Z]/.test(passwordData.new_password), 'One uppercase'],
                      [/[0-9]/.test(passwordData.new_password), 'One number'],
                      [/[^A-Za-z0-9]/.test(passwordData.new_password), 'One symbol'],
                    ].map(([ok, txt], i) => (
                      <li key={i} className={ok ? 'text-emerald-400' : ''}>· {txt}</li>
                    ))}
                  </ul>
                </div>
              )}
              <LuxuryButton onClick={handleUpdatePassword} icon={Lock} tone="primary" accent="#8A2BE2">Update Password</LuxuryButton>
            </div>
          </BentoCard>

          {/* Danger */}
          <BentoCard accent="#F43F5E" tone="danger">
            <BentoHeader icon={AlertTriangle} title="Danger Zone" subtitle="Irreversible actions" accent="#F43F5E" />
            <Divider />
            <div className="px-6 py-5">
              <div className="flex items-center justify-between p-4 rounded-2xl bg-rose-500/[0.06] border border-rose-500/20">
                <div>
                  <p className="text-white font-medium text-sm">Sign Out Everywhere</p>
                  <p className="text-white/50 text-xs mt-0.5">Terminate every active session on every device</p>
                </div>
                <LuxuryButton tone="danger" icon={LogOut}
                  onClick={() => {
                    if (window.confirm('Sign out from all devices?')) {
                      try { localStorage.clear(); } catch {}
                      toast.success('Signed out everywhere');
                      onLogout && onLogout();
                    }
                  }}>Sign Out All</LuxuryButton>
              </div>
            </div>
          </BentoCard>
        </div>
      )}

      {/* ─── SECURITY ─── */}
      {activeTab === 'security' && (
        <div className="space-y-4 max-w-3xl" data-testid="tab-security">
          {/* 2FA */}
          <BentoCard accent="#8A2BE2">
            <BentoHeader icon={Fingerprint} title="Two-Factor Authentication" subtitle="Extra layer of protection" accent="#8A2BE2"
              right={<span className={`px-2.5 py-1 rounded-full text-[9px] font-semibold tracking-[0.15em] uppercase border ${twoFactorEnabled ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30' : 'text-white/50 bg-white/[0.04] border-white/[0.10]'}`}>
                {twoFactorEnabled ? 'Enabled' : 'Off'}
              </span>} />
            <Divider />
            <div className="px-6 py-5">
              {twoFactorEnabled ? (
                <>
                  <div className="flex items-center gap-4 p-4 rounded-2xl bg-emerald-500/[0.06] border border-emerald-500/25 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-emerald-200 text-sm font-medium">Your account is protected</p>
                      <p className="text-emerald-400/70 text-xs mt-0.5">Authenticator app linked · sign-ins require a code</p>
                    </div>
                  </div>
                  <LuxuryButton tone="danger" icon={XCircle} onClick={handle2FADisable} className="w-full">Disable 2FA</LuxuryButton>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-4 p-4 rounded-2xl bg-amber-500/[0.06] border border-amber-500/25 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-amber-200 text-sm font-medium">Enhance your security</p>
                      <p className="text-amber-400/70 text-xs mt-0.5">Pair a mobile authenticator to require a 6-digit code at every sign-in</p>
                    </div>
                  </div>
                  <LuxuryButton tone="primary" accent="#8A2BE2" icon={Smartphone} className="w-full" onClick={() => setShow2FASetup(true)}>Enable 2FA</LuxuryButton>
                </>
              )}
            </div>
          </BentoCard>

          {/* Sessions */}
          <BentoCard accent="#00E5FF">
            <BentoHeader icon={Laptop} title="Active Sessions" subtitle="Devices signed in" accent="#00E5FF"
              right={<span className="px-2.5 py-1 rounded-full text-[9px] font-semibold tracking-[0.15em] uppercase border text-cyan-300 bg-cyan-500/10 border-cyan-500/30">{sessions.length || 1} active</span>} />
            <Divider />
            <div className="px-6 py-5">
              <div className="flex items-center justify-between p-4 rounded-2xl bg-cyan-500/[0.06] border border-cyan-500/25">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                    <Monitor className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">This device</p>
                    <p className="text-white/50 text-xs">Current browser session</p>
                  </div>
                </div>
                <span className="text-[9px] tracking-[0.15em] uppercase font-medium text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2.5 py-1 rounded-full">Active now</span>
              </div>
            </div>
          </BentoCard>

          {/* Login history */}
          <BentoCard accent="#F472B6">
            <BentoHeader icon={History} title="Login History" subtitle="Recent sign-in attempts" accent="#F472B6"
              right={<button onClick={() => setShowLoginHistory(true)} className="text-[10px] tracking-[0.18em] uppercase text-pink-300 hover:text-pink-200 font-medium">View all →</button>} />
            <Divider />
            <div className="px-6 py-5">
              {loginHistory.length === 0 ? (
                <p className="text-center text-white/40 text-sm py-6">No login history yet</p>
              ) : (
                <div className="space-y-2">
                  {loginHistory.slice(0, 4).map((l, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${l.success ? 'bg-emerald-500/15 border border-emerald-500/25' : 'bg-rose-500/15 border border-rose-500/25'}`}>
                          {l.success ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-rose-400" />}
                        </div>
                        <div>
                          <p className="text-white text-xs">{formatDate(l.timestamp)}</p>
                          <p className="text-white/40 text-[10px]">IP {l.ip_address || 'unknown'}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-wide uppercase ${l.success ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30' : 'bg-rose-500/10 text-rose-300 border border-rose-500/30'}`}>
                        {l.success ? 'Success' : 'Failed'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </BentoCard>
        </div>
      )}

      {/* ─── PREFERENCES ─── */}
      {activeTab === 'preferences' && (
        <div className="space-y-4 max-w-3xl" data-testid="tab-preferences">
          {/* Notifications */}
          <BentoCard accent="#F59E0B">
            <BentoHeader icon={Bell} title="Notifications" subtitle="Ping preferences" accent="#F59E0B" />
            <Divider />
            <div className="px-6 py-5 space-y-2.5">
              {[
                { key: 'notifications_email', icon: Mail, label: 'Email notifications', desc: 'Digest, mentions, HR alerts', hex: '#38BDF8' },
                { key: 'notifications_push',  icon: Bell, label: 'Browser push',        desc: 'Real-time in-app pings',    hex: '#F59E0B' },
                { key: 'show_online_status',  icon: Activity, label: 'Show online status', desc: 'Let teammates see when you\'re active', hex: '#10B981' },
                { key: 'sound_enabled',       icon: Volume2, label: 'Sound effects',    desc: 'Ping on messages, chimes on calls',       hex: '#8A2BE2' },
              ].map(p => (
                <div key={p.key} className="flex items-center justify-between p-3.5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${p.hex}18`, border: `1px solid ${p.hex}30` }}>
                      <p.icon className="w-4 h-4" style={{ color: p.hex }} />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{p.label}</p>
                      <p className="text-white/40 text-[11px]">{p.desc}</p>
                    </div>
                  </div>
                  <Switch checked={preferences[p.key]} onCheckedChange={(v) => setPreferences({ ...preferences, [p.key]: v })} data-testid={`pref-${p.key}`} />
                </div>
              ))}

              {/* Quiet hours */}
              <div className="p-3.5 rounded-2xl bg-gradient-to-br from-indigo-500/[0.06] to-violet-500/[0.04] border border-indigo-400/20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-indigo-500/15 border border-indigo-400/30 flex items-center justify-center">
                      <Moon className="w-4 h-4 text-indigo-300" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Quiet hours</p>
                      <p className="text-white/40 text-[11px]">Silence push during your off hours</p>
                    </div>
                  </div>
                  <Switch checked={!!preferences.quiet_hours_enabled} onCheckedChange={(v) => setPreferences({ ...preferences, quiet_hours_enabled: v })} data-testid="pref-quiet-hours" />
                </div>
                {preferences.quiet_hours_enabled && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[9px] tracking-[0.2em] uppercase text-white/40 block mb-1">From</label>
                      <input type="time" value={preferences.quiet_hours_start || '22:00'} onChange={(e) => setPreferences({ ...preferences, quiet_hours_start: e.target.value })}
                        className="w-full px-3 py-2 bg-black/50 border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-indigo-400/40" />
                    </div>
                    <div>
                      <label className="text-[9px] tracking-[0.2em] uppercase text-white/40 block mb-1">Until</label>
                      <input type="time" value={preferences.quiet_hours_end || '08:00'} onChange={(e) => setPreferences({ ...preferences, quiet_hours_end: e.target.value })}
                        className="w-full px-3 py-2 bg-black/50 border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-indigo-400/40" />
                    </div>
                  </div>
                )}
              </div>

              {/* Focus */}
              <div className="flex items-center justify-between p-3.5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/30 flex items-center justify-center">
                    <Target className="w-4 h-4 text-fuchsia-300" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Focus mode</p>
                    <p className="text-white/40 text-[11px]">Hide everything but urgent alerts</p>
                  </div>
                </div>
                <Switch checked={!!preferences.focus_mode} onCheckedChange={(v) => setPreferences({ ...preferences, focus_mode: v })} data-testid="pref-focus-mode" />
              </div>
            </div>
          </BentoCard>

          {/* Locale */}
          <BentoCard accent="#00E5FF">
            <BentoHeader icon={Globe} title="Locale" subtitle="Language & timezone" accent="#00E5FF" />
            <Divider />
            <div className="px-6 py-5 space-y-3">
              <div className="flex items-center justify-between p-3.5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-cyan-300" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Timezone</p>
                    <p className="text-white/40 text-[11px]">Currently: <span className="text-cyan-300 font-mono">{preferences.timezone || 'UTC'}</span></p>
                  </div>
                </div>
                <LuxuryButton tone="outline" accent="#00E5FF" icon={Zap} data-testid="pref-detect-tz"
                  onClick={() => { const tz = Intl.DateTimeFormat().resolvedOptions().timeZone; setPreferences({ ...preferences, timezone: tz }); toast.success(`Detected ${tz}`); }}>
                  Auto-detect
                </LuxuryButton>
              </div>
            </div>
          </BentoCard>

          <LuxuryButton tone="primary" accent="#00E5FF" icon={Save} onClick={savePreferences} className="w-full" data-testid="save-preferences-btn">Save Preferences</LuxuryButton>
        </div>
      )}

      {/* ─── ACTIVITY ─── */}
      {activeTab === 'activity' && (
        <div className="space-y-4 max-w-4xl" data-testid="tab-activity">
          {/* Quick actions */}
          <BentoCard accent="#00E5FF">
            <BentoHeader icon={Zap} title="Quick Actions" subtitle="One-tap shortcuts" accent="#00E5FF" />
            <Divider />
            <div className="px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { id: 'pwd',        label: 'Change Password', icon: Key,       hex: '#F59E0B', onClick: () => setActiveTab('account') },
                { id: '2fa',        label: 'Setup 2FA',       icon: Shield,    hex: '#00E5FF', onClick: () => setActiveTab('security') },
                { id: 'theme',      label: 'Theme & Prefs',   icon: Palette,   hex: '#8A2BE2', onClick: () => setActiveTab('preferences') },
                { id: 'logout-all', label: 'Sign Out All',    icon: LogOut,    hex: '#F43F5E', onClick: () => {
                    if (window.confirm('Sign out from all devices?')) {
                      try { localStorage.clear(); } catch {}
                      toast.success('Signed out everywhere');
                      onLogout && onLogout();
                    }
                  } },
              ].map(a => (
                <button key={a.id} onClick={a.onClick} data-testid={`quick-action-${a.id}`}
                  className="group relative overflow-hidden p-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:border-white/[0.15] transition-all text-left">
                  <div className="pointer-events-none absolute -top-6 -right-4 w-24 h-24 rounded-full blur-[60px] opacity-40 group-hover:opacity-70 transition-opacity" style={{ background: a.hex }} />
                  <div className="relative">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform" style={{ background: `${a.hex}18`, border: `1px solid ${a.hex}30` }}>
                      <a.icon className="w-4 h-4" style={{ color: a.hex }} />
                    </div>
                    <p className="text-white text-xs font-medium">{a.label}</p>
                  </div>
                </button>
              ))}
            </div>
          </BentoCard>

          {/* Security health */}
          <BentoCard accent="#10B981">
            {(() => {
              const checks = [!!currentUser?.email, !!twoFactorEnabled, !!profileData.display_name, !!avatarPreview, !!profileData.phone];
              const score = Math.round((checks.filter(Boolean).length / checks.length) * 100);
              const tone = score >= 75 ? '#10B981' : score >= 50 ? '#F59E0B' : '#F43F5E';
              return (
                <BentoHeader icon={Shield} title="Security Health" subtitle="Account hygiene" accent="#10B981"
                  right={<div className="text-right"><p className="text-2xl font-light tabular-nums" style={{ color: tone }}>{score}%</p><p className="text-[9px] tracking-[0.2em] uppercase text-white/40">Score</p></div>} />
              );
            })()}
            <Divider />
            <div className="px-6 py-5 space-y-2">
              {[
                { id: 'email',    label: 'Email verified',           ok: !!currentUser?.email,               action: 'Add email',    tab: 'account' },
                { id: '2fa',      label: 'Two-factor authentication', ok: !!twoFactorEnabled,                 action: 'Enable',       tab: 'security' },
                { id: 'name',     label: 'Display name set',          ok: !!profileData.display_name,         action: 'Add name',     tab: 'profile' },
                { id: 'avatar',   label: 'Profile photo uploaded',    ok: !!avatarPreview,                    action: 'Upload photo', tab: 'profile' },
                { id: 'recovery', label: 'Recovery phone configured', ok: !!profileData.phone,                action: 'Add phone',    tab: 'profile' },
              ].map(c => (
                <div key={c.id} className={`flex items-center gap-3 p-3 rounded-xl border ${c.ok ? 'bg-emerald-500/[0.04] border-emerald-500/20' : 'bg-white/[0.02] border-white/[0.06]'}`} data-testid={`security-check-${c.id}`}>
                  {c.ok ? <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <CircleDot className="w-4 h-4 text-white/30 flex-shrink-0" />}
                  <span className={`text-xs flex-1 ${c.ok ? 'text-white/85' : 'text-white/50'}`}>{c.label}</span>
                  {!c.ok && (
                    <button onClick={() => setActiveTab(c.tab)} className="text-[10px] tracking-[0.15em] uppercase text-cyan-300 hover:text-cyan-200 font-medium">
                      {c.action} →
                    </button>
                  )}
                </div>
              ))}
            </div>
          </BentoCard>

          {/* Privacy */}
          <BentoCard accent="#A855F7">
            <BentoHeader icon={Eye} title="Privacy" subtitle="Who sees what" accent="#A855F7" />
            <Divider />
            <div className="px-6 py-5 space-y-2">
              {[
                { id: 'show_status',   label: 'Show online status',        desc: 'Let teammates see when you\'re online' },
                { id: 'show_email',    label: 'Show email in directory',   desc: 'Display your email on the staff directory' },
                { id: 'show_phone',    label: 'Show phone in directory',   desc: 'Display your phone number to staff' },
                { id: 'allow_dms',     label: 'Allow direct messages',     desc: 'Let staff send you direct messages' },
                { id: 'show_birthday', label: 'Show birthday in feed',     desc: 'Let your birthday show in the team feed' },
              ].map(p => (
                <div key={p.id} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium">{p.label}</p>
                    <p className="text-white/40 text-[11px]">{p.desc}</p>
                  </div>
                  <Switch checked={profileData[`privacy_${p.id}`] !== false} onCheckedChange={(c) => setProfileData({ ...profileData, [`privacy_${p.id}`]: c })} data-testid={`privacy-toggle-${p.id}`} />
                </div>
              ))}
            </div>
          </BentoCard>

          {/* Data export */}
          <BentoCard accent="#00E5FF">
            <BentoHeader icon={Download} title="Data Management" subtitle="Own your data" accent="#00E5FF" />
            <Divider />
            <div className="px-6 py-5">
              <div className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                <div>
                  <p className="text-white text-sm font-medium">Export profile snapshot</p>
                  <p className="text-white/40 text-[11px]">JSON of your profile, preferences and settings</p>
                </div>
                <LuxuryButton tone="outline" accent="#00E5FF" icon={Download} data-testid="export-data-btn"
                  onClick={() => {
                    try {
                      const blob = new Blob([JSON.stringify({ exported_at: new Date().toISOString(), username: currentUser?.username, profile: profileData, preferences }, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = `${currentUser?.username || 'profile'}-export.json`;
                      a.click(); URL.revokeObjectURL(url);
                      toast.success('Profile exported');
                    } catch { toast.error('Export failed'); }
                  }}>Export JSON</LuxuryButton>
              </div>
            </div>
          </BentoCard>
        </div>
      )}

      {/* ─── EMPLOYMENT ─── */}
      {activeTab === 'employment' && (
        <div className="space-y-4 max-w-3xl" data-testid="tab-employment">
          <BentoCard accent="#38BDF8">
            <BentoHeader icon={Briefcase} title="Employment Details" subtitle="Work snapshot" accent="#38BDF8" />
            <Divider />
            <div className="px-6 py-5 grid grid-cols-2 gap-3">
              {[
                { label: 'Job Title',   value: currentUser?.job_title || 'Not assigned',                       hex: '#38BDF8', icon: Briefcase },
                { label: 'Department',  value: profileData.department || currentUser?.department || 'Not set', hex: '#A855F7', icon: Building },
                { label: 'Employee ID', value: currentUser?.employee_id || currentUser?.username,              hex: '#F59E0B', icon: BadgeCheck },
                { label: 'Status',      value: 'Active',                                                       hex: '#10B981', icon: CheckCircle },
              ].map(s => (
                <div key={s.label} className="relative overflow-hidden rounded-2xl bg-white/[0.02] border border-white/[0.06] p-4">
                  <div className="pointer-events-none absolute -top-8 -right-4 w-24 h-24 rounded-full blur-[60px] opacity-40" style={{ background: s.hex }} />
                  <div className="relative flex items-center gap-2 mb-2">
                    <s.icon className="w-3.5 h-3.5" style={{ color: s.hex }} />
                    <p className="text-[9px] tracking-[0.2em] uppercase text-white/40 font-medium">{s.label}</p>
                  </div>
                  <p className="text-white text-sm font-medium">{s.value}</p>
                </div>
              ))}
            </div>
          </BentoCard>

          <BentoCard accent="#F97316" tone={myResignation ? 'default' : 'default'}>
            <BentoHeader icon={UserMinus} title="Resignation" subtitle="Manage employment status" accent="#F97316" />
            <Divider />
            <div className="px-6 py-5">
              {myResignation ? (
                <>
                  <div className="p-4 rounded-2xl bg-orange-500/[0.06] border border-orange-500/25 mb-3">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white text-sm font-medium">Resignation Status</span>
                      <span className={`px-2.5 py-1 rounded-full text-[9px] font-semibold tracking-[0.15em] uppercase ${
                        myResignation.status === 'pending' ? 'bg-amber-500/10 text-amber-300 border border-amber-500/30'
                        : myResignation.status === 'approved' ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                        : 'bg-rose-500/10 text-rose-300 border border-rose-500/30'
                      }`}>{myResignation.status?.toUpperCase()}</span>
                    </div>
                    <div className="text-xs space-y-1">
                      <p className="text-white/50">Submitted: <span className="text-white/80">{formatDate(myResignation.submitted_at)}</span></p>
                      <p className="text-white/50">Last Day: <span className="text-white/80">{myResignation.last_working_day}</span></p>
                    </div>
                  </div>
                  {myResignation.status === 'pending' && (
                    <LuxuryButton tone="outline" accent="#F97316" onClick={handleWithdrawResignation} className="w-full">Withdraw Resignation</LuxuryButton>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-start gap-4 p-4 rounded-2xl bg-orange-500/[0.06] border border-orange-500/20 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-orange-200 text-sm font-medium">Considering leaving?</p>
                      <p className="text-orange-400/70 text-xs mt-0.5">Submitting notifies your manager and HR for formal review.</p>
                    </div>
                  </div>
                  <LuxuryButton tone="outline" accent="#F97316" icon={UserMinus} onClick={() => setShowResignModal(true)} className="w-full">I wish to resign</LuxuryButton>
                </>
              )}
            </div>
          </BentoCard>
        </div>
      )}

      {/* ─── WARNINGS ─── */}
      {activeTab === 'warnings' && (
        <WarningsTimelineTab username={currentUser?.username} />
      )}

      {/* ═══════════════════════════════════════════
          MODALS — all JewelModal
          ═══════════════════════════════════════════ */}
      <JewelModal open={showResignModal} onOpenChange={setShowResignModal} icon={UserMinus} title="Submit Resignation"
        subtitle="Formally notify HR of your intent to leave" accent="amber" size="md" testid="resign-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowResignModal(false)}>Cancel</JewelButton>
          <JewelButton accent="amber" icon={Send} onClick={handleSubmitResignation} disabled={!resignationData.reason || !resignationData.last_working_day}>Submit resignation</JewelButton>
        </>}>
        <div className="space-y-3">
          <JewelField label="Reason" required>
            <JewelSelect value={resignationData.reason} onChange={(e) => setResignationData({ ...resignationData, reason: e.target.value })}>
              <option value="">Select reason</option>
              <option value="new_opportunity">New opportunity</option>
              <option value="personal">Personal reasons</option>
              <option value="relocation">Relocation</option>
              <option value="health">Health</option>
              <option value="other">Other</option>
            </JewelSelect>
          </JewelField>
          <JewelField label="Last working day" required>
            <JewelInput type="date" value={resignationData.last_working_day} onChange={(e) => setResignationData({ ...resignationData, last_working_day: e.target.value })} min={new Date().toISOString().split('T')[0]} />
          </JewelField>
          <JewelField label="Feedback (optional)">
            <JewelTextarea rows={3} value={resignationData.feedback} onChange={(e) => setResignationData({ ...resignationData, feedback: e.target.value })} placeholder="Anything else you'd like HR to know…" />
          </JewelField>
        </div>
      </JewelModal>

      <JewelModal open={showLoginHistory} onOpenChange={setShowLoginHistory} icon={History} title="Full login history"
        subtitle="Every sign-in attempt on your account" accent="violet" size="md" testid="login-history-modal"
        footer={<JewelButton variant="ghost" onClick={() => setShowLoginHistory(false)}>Close</JewelButton>}>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto jewel-scroll pr-1">
          {loginHistory.length === 0 ? (
            <div className="text-center py-10">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/[0.03] border border-white/[0.08] mb-3">
                <History className="w-6 h-6 text-white/40" strokeWidth={1.6} />
              </div>
              <p className="text-white/60">No login history available</p>
            </div>
          ) : loginHistory.map((login, i) => (
            <div key={i} className="relative overflow-hidden flex items-center gap-3 p-3 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
              <span className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${login.success ? 'from-emerald-400 to-teal-500' : 'from-rose-400 to-red-500'}`} />
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${login.success ? 'bg-emerald-500/[0.10] border border-emerald-500/25' : 'bg-rose-500/[0.10] border border-rose-500/25'}`}>
                {login.success ? <CheckCircle className="w-4 h-4 text-emerald-300" /> : <AlertCircle className="w-4 h-4 text-rose-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/90 text-sm">{formatDate(login.timestamp)}</p>
                <p className="text-white/40 text-[11px] mt-0.5">IP {login.ip_address || 'unknown'}</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wide uppercase ${login.success ? 'bg-emerald-500/[0.12] border border-emerald-500/30 text-emerald-200' : 'bg-rose-500/[0.12] border border-rose-500/30 text-rose-200'}`}>{login.success ? 'Success' : 'Failed'}</span>
            </div>
          ))}
        </div>
      </JewelModal>

      <JewelModal open={show2FASetup} onOpenChange={(open) => { setShow2FASetup(open); if (!open) { setQrCodeData(null); setTotpSecret(''); setVerifyCode(''); } }}
        icon={Fingerprint} title="Two-factor authentication"
        subtitle={qrCodeData ? 'Scan the code with your authenticator app' : 'Add an extra layer of security'}
        accent="violet" size="md" testid="2fa-modal"
        footer={qrCodeData
          ? <>
              <JewelButton variant="ghost" onClick={() => setShow2FASetup(false)}>Cancel</JewelButton>
              <JewelButton accent="violet" icon={Shield} onClick={verify2FASetup} disabled={verifyCode.length !== 6 || processing2FA} data-testid="2fa-verify-setup-btn">Verify &amp; enable</JewelButton>
            </>
          : <JewelButton variant="ghost" onClick={() => setShow2FASetup(false)}>Close</JewelButton>}>
        {processing2FA ? (
          <div className="text-center py-10">
            <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-400 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-white/60">Verifying your code…</p>
          </div>
        ) : qrCodeData ? (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="p-3 rounded-2xl bg-white shadow-[0_18px_40px_-14px_rgba(139,92,246,0.6)]">
                <img src={qrCodeData} alt="2FA QR" className="w-44 h-44" data-testid="2fa-qr-code" />
              </div>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">Manual entry secret</p>
              <p className="text-white font-mono text-sm break-all select-all tracking-wider" data-testid="2fa-secret">{totpSecret}</p>
            </div>
            <JewelField label="Enter 6-digit code from your app">
              <input type="text" inputMode="numeric" maxLength={6} value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 bg-black/40 border border-white/[0.10] rounded-xl text-white text-center text-xl font-mono tracking-[0.5em] focus:border-purple-400/40 focus:outline-none placeholder-white/25"
                placeholder="000000" autoFocus data-testid="2fa-verify-input" />
            </JewelField>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="p-4 rounded-2xl bg-gradient-to-br from-violet-500/[0.10] to-pink-500/[0.06] border border-violet-400/25">
              <p className="text-violet-200 text-sm leading-relaxed">Pair Starnight with Google Authenticator, Authy or 1Password. From then on you'll enter a 6-digit code alongside your password.</p>
            </div>
            {[
              ['1', 'Install an authenticator app on your phone'],
              ['2', 'Tap "Set up authenticator" below and scan the QR'],
              ['3', 'Enter the 6-digit code to activate'],
            ].map(([n, txt]) => (
              <div key={n} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                <span className="w-7 h-7 rounded-full bg-violet-500/[0.15] border border-violet-400/30 flex items-center justify-center text-violet-200 text-xs font-bold flex-shrink-0">{n}</span>
                <p className="text-white/80 text-sm pt-1">{txt}</p>
              </div>
            ))}
            <JewelButton accent="violet" icon={Smartphone} onClick={initiate2FASetup} data-testid="start-2fa-setup-btn" className="w-full !justify-center">Set up authenticator</JewelButton>
            <p className="text-center text-white/35 text-[11px]">You'll need this code every time you sign in.</p>
          </div>
        )}
      </JewelModal>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   WARNINGS TIMELINE TAB (untouched — already premium)
   ══════════════════════════════════════════════════════════════════════ */
const WarningsTimelineTab = ({ username }) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  React.useEffect(() => {
    if (!username) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const axios = (await import('axios')).default;
        const r = await axios.get(`${BACKEND}/api/hr-notices/warnings/${username}/timeline`, { headers: { Authorization: `Bearer ${token}` } });
        setData(r.data);
      } catch {}
      setLoading(false);
    })();
  }, [username, BACKEND]);

  const typeMeta = {
    warning_verbal:        { label: 'Verbal Warning',        hex: '#F59E0B', bar: 'from-amber-400 to-orange-500', icon: AlertCircle },
    warning_written:       { label: 'Written Warning',       hex: '#F43F5E', bar: 'from-rose-400 to-red-500',     icon: ShieldAlert },
    warning_final:         { label: 'Final Written Warning', hex: '#F43F5E', bar: 'from-rose-500 to-red-600',     icon: ShieldAlert },
    outcome_final_warning: { label: 'Outcome · Final',       hex: '#F43F5E', bar: 'from-rose-500 to-red-600',     icon: ShieldAlert },
  };

  if (loading) return <div className="text-center py-14 text-white/40 text-sm">Loading HR record…</div>;

  const active = data?.active || 0;
  const expired = data?.expired || 0;
  const warnings = data?.warnings || [];

  return (
    <div className="max-w-4xl space-y-6">
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'On record',  value: warnings.length, hex: '#00E5FF', icon: FileText },
          { label: 'Active',     value: active,          hex: '#F43F5E', icon: ShieldAlert },
          { label: 'Expired',    value: expired,         hex: '#10B981', icon: CheckCircle },
        ].map((k, i) => (
          <div key={i} className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0A0A12]/60 backdrop-blur-md p-4">
            <div className="pointer-events-none absolute -top-8 -right-8 w-24 h-24 rounded-full blur-3xl opacity-30" style={{ background: k.hex }} />
            <div className="relative flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl border border-white/[0.08] flex items-center justify-center" style={{ background: `${k.hex}20` }}>
                <k.icon className="w-4 h-4" style={{ color: k.hex }} />
              </div>
              <div>
                <p className="text-2xl font-light text-white tabular-nums leading-none">{k.value}</p>
                <p className="text-[10px] tracking-[0.15em] uppercase text-white/40 mt-1.5">{k.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-[#0A0A12]/60 backdrop-blur-md p-5">
        <div className="pointer-events-none absolute -top-20 right-1/4 w-60 h-40 bg-rose-500/[0.06] rounded-full blur-[100px]" />
        <div className="relative flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-rose-500/15 border border-rose-500/25 flex items-center justify-center">
            <ShieldAlert className="w-4 h-4 text-rose-300" />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/40">Personal record</p>
            <h3 className="text-white text-base font-medium">Warning Timeline</h3>
          </div>
        </div>

        {warnings.length === 0 ? (
          <div className="text-center py-14">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/[0.08] border border-emerald-500/20 mb-4">
              <CheckCircle className="w-7 h-7 text-emerald-300" strokeWidth={1.5} />
            </div>
            <p className="text-white/70 tracking-wide">A clean record</p>
            <p className="text-white/35 text-xs mt-1">No warnings on file — keep it that way.</p>
          </div>
        ) : (
          <div className="relative space-y-3">
            <div className="absolute left-[19px] top-2 bottom-2 w-px bg-gradient-to-b from-rose-400/40 via-white/[0.06] to-transparent" />
            {warnings.map((w) => {
              const meta = typeMeta[w.type] || typeMeta.warning_written;
              const Icon = meta.icon;
              const created = w.created_at ? new Date(w.created_at) : null;
              const expiry = w.expiry_at ? new Date(w.expiry_at) : null;
              const total = created && expiry ? (expiry - created) : 1;
              const elapsed = created ? Math.min(total, Date.now() - created.getTime()) : 0;
              const progressPct = total > 0 ? Math.min(100, (elapsed / total) * 100) : 0;
              return (
                <div key={w.id} className={`relative pl-12 pr-4 py-4 rounded-2xl border transition-all ${
                  w.is_active ? 'border-rose-400/25 bg-rose-500/[0.04]' : 'border-white/[0.06] bg-white/[0.02] opacity-70'
                }`}>
                  <div className={`absolute left-[7px] top-4 w-6 h-6 rounded-full border-2 flex items-center justify-center z-10 ${
                    w.is_active ? 'border-rose-400 bg-rose-500/20 shadow-[0_0_12px_rgba(244,63,94,0.5)]' : 'border-white/20 bg-white/[0.04]'
                  }`}>
                    <Icon className="w-3 h-3" style={{ color: w.is_active ? meta.hex : '#94A3B8' }} />
                  </div>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.1em] uppercase border" style={{ color: meta.hex, background: `${meta.hex}12`, borderColor: `${meta.hex}30` }}>
                          {meta.label}
                        </span>
                        {w.is_active
                          ? <span className="inline-flex items-center gap-1 text-rose-300 text-[9px] tracking-[0.15em] uppercase"><span className="w-1 h-1 rounded-full bg-rose-400 animate-pulse" /> Active · {w.days_remaining}d left</span>
                          : <span className="inline-flex items-center gap-1 text-emerald-300 text-[9px] tracking-[0.15em] uppercase"><CheckCircle className="w-2.5 h-2.5" /> Expired · off record</span>}
                      </div>
                      <p className="text-white text-sm font-medium">{w.title}</p>
                      <p className="text-white/40 text-[11px] mt-0.5">
                        Issued {created ? created.toLocaleDateString('en-GB') : '—'} · Expires {expiry ? expiry.toLocaleDateString('en-GB') : '—'} · by {w.created_by_name}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                    <div className={`h-full transition-all bg-gradient-to-r ${meta.bar}`} style={{ width: `${progressPct}%`, opacity: w.is_active ? 1 : 0.4 }} />
                  </div>
                  <div className="flex justify-between text-[9px] text-white/30 mt-1 tracking-wider">
                    <span>Issued</span><span>{Math.round(progressPct)}%</span><span>Expires</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileAndSettingsPage;
